import * as React from 'react';

import "./App.css";

import { JSONSchema7 } from 'json-schema';

import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ButtonGroup from '@mui/material/ButtonGroup';

import { useTheme } from '@mui/material/styles';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinkIcon from '@mui/icons-material/Link';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RuleIcon from '@mui/icons-material/Rule';

import { JsonForms } from '@jsonforms/react';
import { Translator } from '@jsonforms/core';
import {
	materialRenderers,
	materialCells,
} from '@jsonforms/material-renderers';

import schemaJson from './finalthesis.schema.json';
import uischemaJson from './finalthesis.uischema.json';
import examiners from './examiners.json';
import * as Form from './finalthesis';
import * as Data from './DataRepresentation';
import { fillIn, fillInJsxParagraphed, fillInString, translation } from './I18n';
import * as PdfForm from './PdfForm';
import HelpDialog from './HelpDialog';
import ValidationDialog from './ValidationDialog';
import { ErrorObject } from 'ajv';
import { isJSONSchema7 } from './DataRepresentation';

import ScrollToTop from './ScrollToTop';
import Fab from '@mui/material/Fab';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';


const jsonFormsConfig = {
	restrict: false,
	trim: false,
	showUnfocusedDescription: true,
	hideRequiredAsterisk: false
}

const DOWNLOAD = 'download';

const PRINT = 'print';

// Define a type for the component props
interface AppProps {
}

// Use the 'FC' (Functional Component) generic from React, with our props type
const FinalThesisApp: React.FC<AppProps> = (_props: AppProps) => {
	const [formData, setFormData] = React.useState<Form.AnmeldungDerAbschlussarbeit>(Data.getInitialFormData());
	const [errors, setErrors] = React.useState<ErrorObject[]>([]);
	const [snackbarOpen, setSnackbarOpen] = React.useState<boolean>(false);
	const [warningDialogOpen, setWarningDialogOpen] = React.useState<boolean>(false);
	const [warningMessage, setWarningMessage] = React.useState<string>("");
	const [warningContinueAction, setWarningContinueAction] = React.useState('');
	const [helpDialogOpen, setHelpDialogOpen] = React.useState(false);
	const [validationDialogOpen, setValidationDialogOpen] = React.useState(false);
	const [customValidationErrors, setCustomValidationErrors] = React.useState<ErrorObject[]>([]);

	const locale = Data.getInitialLang();
	
	const SUPERVISOR_OTHER = "Andere(r)";
	schemaJson.properties.supervisor.enum = [...examiners, SUPERVISOR_OTHER];
	schemaJson.properties.second_supervisor.enum = [...examiners, SUPERVISOR_OTHER];
	
	const translate_function: Translator = translation(locale, schemaJson as JSONSchema7) as Translator;

	localStorage.setItem(Data.LOCAL_STORE_KEY_FORM_DATA, JSON.stringify(formData));
	localStorage.setItem(Data.LOCAL_STORE_KEY_LANG, locale);

	const onDownloadDraft = () => {
		var degree = formData.degree ? formData.degree : "degree";
		var lastname = formData.lastname ? formData.lastname : "lastname";
		var firstname = formData.firstname ? formData.firstname : "firstname";

		var filename = degree + '_' + lastname.trim().replaceAll(' ', '-') + '_' + firstname.trim().replaceAll(' ', '-') + '_draft.pdf';

		var dd = PdfForm.pdfFormDocumentDefinition(formData, false, translate_function, locale);
		PdfForm.download(dd, filename);
	};

	const onPrintDraft = () => {
		var dd = PdfForm.pdfFormDocumentDefinition(formData, false, translate_function, locale);
		PdfForm.print(dd);
	};

	const downloadFinal = () => {
		var degree = formData.degree ? formData.degree : "degree";
		var lastname = formData.lastname ? formData.lastname : "lastname";
		var firstname = formData.firstname ? formData.firstname : "firstname";

		var filename = degree + '_' + lastname.trim().replaceAll(' ', '-') + '_' + firstname.trim().replaceAll(' ', '-') + '.pdf';

		var dd = PdfForm.pdfFormDocumentDefinition(formData, true, translate_function, locale);
		PdfForm.download(dd, filename);
	}

	const onDownloadFinal = () => {
		if (errors.length > 0 || customValidationErrors.length > 0) {
			setWarningMessage(translate_function("page.warning.download_on_incomplete", 'No warning message'));
			setWarningContinueAction(DOWNLOAD);
			setWarningDialogOpen(true);
		} else {
			downloadFinal()
		}
	};

	const printFinal = () => {
		var dd = PdfForm.pdfFormDocumentDefinition(formData, true, translate_function, locale);
		PdfForm.print(dd);
	}

	const onPrintFinal = () => {
		if (errors.length > 0 || customValidationErrors.length > 0) {
			setWarningMessage(translate_function("page.warning.print_on_incomplete", 'No warning message'));
			setWarningContinueAction(PRINT);
			setWarningDialogOpen(true);
		} else {
			printFinal();
		}
	};

	const handleCloseWarningDialog = () => {
		setWarningDialogOpen(false);
	}

	const handleContinue = () => {
		handleCloseWarningDialog();
		if (warningContinueAction === DOWNLOAD) {
			downloadFinal();
		} else if (warningContinueAction === PRINT) {
			printFinal();
		}
	}

	React.useEffect(() => {
		const customErrors: ErrorObject[] = [];
		const today = new Date(new Date().toDateString());
		const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
		
		//^[\u0020-\u007e\u00a0-\u00ff]+$
		
		const formDataMO = schemaJson as JSONSchema7;
		const checkStringProperties = (metaObject : JSONSchema7, data: { [x: string]: any; } | any, path: string) => {
			if (metaObject.type === 'string' && data !== undefined) {
					const str = data as string;
					var message = '';
					for (let i = 0; i < str.length; i++) {
						// legal characters for iso 8859-1 (Latin-1) encoding, as requuired for QR code
						//\u0020-\u007e\u00a0-\u00ff
  						const c = str.charAt(i);
  						if (!(
							  (c >= '\u0020' && c <= '\u007e') ||
							  (c >= '\u00a0' && c <= '\u00ff')
							  )) {
								  if (message !=='') {
									  message = message + ' ';
								  }
								  
								  message += fillInString(
									translate_function('invalid.allowed_char_at_index'),
								  	{
										index: i.toString(),
								  		character: c.toString()
								  	});
						  }
					}
					if (message !== '') {
						customErrors.push({
							// AJV style path to the property in the schema
							instancePath: path,
							// message to display
							message: message + ' ' + translate_function('invalid.allowed_char_explanation'),
							schemaPath: '',
							keyword: '',
							params: {},
						});
					}
			}
			if (metaObject.properties !== undefined) {
				for (const key in metaObject.properties) {
					if (Data.isJSONSchema7(metaObject.properties[key])) {
						var subMetaObject = (metaObject.properties[key] as JSONSchema7);
						
						checkStringProperties(subMetaObject, data[key], path + '/' + key);
					}
				}
			}			
		}

	checkStringProperties(formDataMO, formData, '');

		if (formData.birthday !== undefined && new Date(formData.birthday) > today) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/birthday',
				// message to display
				message: 'invalid.birthday_in_past',
				schemaPath: '',
				keyword: '',
				params: {},
			});
		} else if (formData.birthday !== undefined && new Date(formData.birthday) > eighteenYearsAgo) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/birthday',
				// message to display
				message: 'invalid.older_than_18',
				schemaPath: '',
				keyword: '',
				params: {},
			});
		}

		if (formData.start_date !== undefined && new Date(formData.start_date) < today) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/start_date',
				// message to display
				message: 'invalid.start_date_in_future',
				schemaPath: '',
				keyword: '',
				params: {},
			});
		}

		let startDateLimit = new Date(today);
		startDateLimit.setDate(startDateLimit.getDate() + 60);
		if (formData.start_date !== undefined && new Date(formData.start_date) > startDateLimit) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/start_date',
				// message to display
				message: 'invalid.start_date_soon',
				schemaPath: '',
				keyword: '',
				params: {},
			});
		}

		if (formData.supervisor === SUPERVISOR_OTHER && formData.supervisor_other === undefined) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/supervisor_other',
				// message to display
				message: 'invalid.required_supervisor_other',
				schemaPath: '',
				keyword: '',
				params: {},
			});
		}

		if (formData.second_supervisor === SUPERVISOR_OTHER && formData.second_supervisor_other === undefined) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/second_supervisor_other',
				// message to display
				message: 'invalid.required_second_supervisor_other',
				schemaPath: '',
				keyword: '',
				params: {},
			});
		}

		const supervisor = formData.supervisor === SUPERVISOR_OTHER ? formData.supervisor_other : formData.supervisor;
		const second_supervisor = formData.second_supervisor === SUPERVISOR_OTHER ? formData.second_supervisor_other : formData.second_supervisor;
		if (supervisor !== undefined && supervisor === second_supervisor) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: formData.supervisor !== SUPERVISOR_OTHER ? '/supervisor' : '/supervisor_other',
				// message to display
				message: 'invalid.distinct_supervisor_and_reviewer',
				schemaPath: '',
				keyword: '',
				params: {},
			});
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: formData.second_supervisor !== SUPERVISOR_OTHER ? '/second_supervisor' : '/second_supervisor_other',
				// message to display
				message: 'invalid.distinct_supervisor_and_reviewer',
				schemaPath: '',
				keyword: '',
				params: {},
			});
		}

		if (formData.exam_regulations === "Andere" && formData.exam_regulations_other === undefined) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/exam_regulations_other',
				// message to display
				message: 'invalid.required_exam_regulations_other',
				schemaPath: '',
				keyword: '',
				params: {},
			});
		}

		var requirementsPropertyName = Data.getRequirementsPropertyName(formData);

		if (requirementsPropertyName === undefined) {
			// studies not fully specified
			var requirementOther = formData.requirements_other;
			if (requirementOther === undefined || requirementOther.fulfilled !== true) {
				customErrors.push({
					// AJV style path to the property in the schema
					instancePath: '/requirements_other/fulfilled',
					// message to display
					message: 'invalid.requirements_other_fulfilled',
					schemaPath: '',
					keyword: '',
					params: {},
				});
			}
			if (requirementOther === undefined || requirementOther.description === undefined || requirementOther.description === "") {
				customErrors.push({
					// AJV style path to the property in the schema
					instancePath: '/requirements_other/description',
					// message to display
					message: 'invalid.required_requirements_other_description',
					schemaPath: '',
					keyword: '',
					params: {},
				});
			}

		} else {

			var requirements = formData[requirementsPropertyName as Data.FormKey] as Form.Requirements;
			var schema: JSONSchema7 = schemaJson as JSONSchema7;
			if (schema.properties !== undefined && isJSONSchema7(schema.properties[requirementsPropertyName])) {
				var requirementPropertiesMO = (schema.properties[requirementsPropertyName] as JSONSchema7).properties;

				for (const key in requirementPropertiesMO) {
					if (key !== "isw_done" && requirements[key] !== true) {
						customErrors.push({
							// AJV style path to the property in the schema
							instancePath: '/' + requirementsPropertyName + '/' + key,
							// message to display
							message: 'invalid.requirement_fulfilled',
							schemaPath: '',
							keyword: '',
							params: {},
						});
						console.log('/' + requirementsPropertyName + '/' + key);
					}
				}
			} else {
				throw new Error('Illegal Schema.');
			}
		}


		setCustomValidationErrors(customErrors);
	}, [formData]);

	const theme = useTheme();

	return (
		<React.Fragment>

			<Grid
				container
				direction="row"
				sx={{
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Box
					component="img"
					sx={{
						width: 9/10,
						maxWidth: { alignItems: 'center' }
					}}
					alt={translate_function("aria.workflow-banner")}
					src="WorkflowBanner.png"
				/>
			</Grid>
			<Box id="back-to-top-anchor">
				<Typography variant="body2">
					{fillInJsxParagraphed(translate_function("page.introduction"), { help_icon: <HelpOutlineIcon aria-label={translate_function("aria.help_icon")} style={{ verticalAlign: 'middle' }} /> })}
				</Typography>
			</Box>
			<Stack spacing={2} direction="column">

				<Stack spacing={2} direction="row" justifyContent="flex-start" sx={{ flexWrap: 'wrap' }}>
					<ButtonGroup variant="contained" aria-label={translate_function("aria.reset.buttongroup")}>
						<Button variant="contained"
							onClick={() => {
								setFormData(Data.EMPTY_FORM_DATA)
							}}
						>{translate_function("page.reset")}</Button>
					</ButtonGroup>

					<ButtonGroup variant="contained" aria-label={translate_function("aria.draft.buttongroup")}>
						<Button variant="contained"
							onClick={() => {
								navigator.clipboard.writeText(Data.editLink(formData, locale));
								setSnackbarOpen(true);
							}}
							startIcon={<LinkIcon style={{ color: theme.palette.primary.contrastText }} />}>
							{translate_function("page.draft")}
							<Snackbar
								open={snackbarOpen}
								autoHideDuration={6000}
								onClose={() => {
									setSnackbarOpen(false);
								}}
							>
								<Alert
									severity="success"
									style={{ textTransform: 'none', color: 'white' }}
								>
									{translate_function("page.link_copied")}
								</Alert>
							</Snackbar>
						</Button>
						<Button onClick={onDownloadDraft} aria-label={translate_function("aria.download-draft")} startIcon={<FileDownloadIcon style={{ color: theme.palette.primary.contrastText }} />}> {translate_function("page.draft")}</Button>
						<Button onClick={onPrintDraft} aria-label={translate_function("aria.print-draft")} startIcon={<PrintIcon style={{ color: theme.palette.primary.contrastText }} />}>{translate_function("page.draft")}</Button>
					</ButtonGroup>

					<ButtonGroup variant="contained" aria-label={translate_function("aria.final.buttongroup")}>
						<Button onClick={onDownloadFinal} aria-label={translate_function("aria.download-final")} startIcon={<FileDownloadIcon style={{ color: theme.palette.primary.contrastText }} />}> {translate_function("page.final")}</Button>
						<Button onClick={onPrintFinal} aria-label={translate_function("aria.print-final")} startIcon={<PrintIcon style={{ color: theme.palette.primary.contrastText }} />}>{translate_function("page.final")}</Button>
					</ButtonGroup>
					<Button variant="contained"
						onClick={() => { setValidationDialogOpen(true) }}
					><RuleIcon /></Button>
					<Button variant="contained"
						onClick={() => { setHelpDialogOpen(true) }}
					><HelpOutlineIcon /></Button>

				</Stack>

				<Box
					sx={{
						borderRadius: 1,
						bgcolor: '#eee',
						p: 1,
					}}
				>
					<JsonForms
						schema={schemaJson}
						uischema={uischemaJson}
						data={formData}
						renderers={materialRenderers}
						cells={materialCells}
						config={jsonFormsConfig}
						onChange={({ data, errors }) => { setFormData(data); setErrors(errors as any[]); }}
						i18n={{
							translate: translate_function,
							locale: locale,
						}}
						additionalErrors={customValidationErrors}
					/>
				</Box>
				<Typography variant="body2">
					{fillInJsxParagraphed(translate_function("page.submission_instructions"), {
						help_icon: <HelpOutlineIcon aria-label={translate_function("aria.help_icon")} style={{ verticalAlign: 'middle' }} />,
						print_icon: <PrintIcon aria-label={translate_function("aria.print_icon")} style={{ verticalAlign: 'middle' }} />
					})}
				</Typography>
				<Typography variant="body2" align="right">{`${Data.FORM_NAME} | ${Data.FORM_VERSION} | ${Data.FORM_DATE}`}</Typography>
			</Stack>
			<Dialog
				open={warningDialogOpen}
				onClose={handleCloseWarningDialog}
				aria-labelledby="incomplete-dialog-title"
				aria-describedby="incomplete-dialog-description"
			>
				<DialogTitle id="incomplete-dialog-title">
					{translate_function("page.dialog.incomplete_title")}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="incomplete-dialog-description">
						{warningMessage}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseWarningDialog}>{translate_function("page.dialog.abort")}</Button>
					<Button onClick={handleContinue} autoFocus>
						{translate_function("page.dialog.continue")}
					</Button>
				</DialogActions>
			</Dialog>
			<HelpDialog open={helpDialogOpen} setOpen={setHelpDialogOpen} translator={translate_function} formData={formData} />
			<ValidationDialog open={validationDialogOpen} setOpen={setValidationDialogOpen} translator={translate_function} errors={errors.concat(customValidationErrors)} />
			<ScrollToTop>
				<Fab size="small" aria-label={translate_function("aria.scroll_to_top")}>
					<KeyboardArrowUpIcon />
				</Fab>
			</ScrollToTop>

		</React.Fragment>
	);

}

export default FinalThesisApp;